import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';
import type { TextMatch } from '../../utils/customerPicker';

interface PersonRowProps {
  /** The avatar, usually a PersonAvatar. */
  leading: React.ReactNode;
  name: string;
  /** Bolds the part of the name that matched the search. */
  highlight?: TextMatch;
  meta?: string;
  metaTone?: 'default' | 'error' | 'muted';
  selected?: boolean;
  /** Pink tint and red outline, for a contact whose save failed. */
  error?: boolean;
  /** Something after the text: a tag, a badge, a retry button. */
  trailing?: React.ReactNode;
  onPress?: () => void;
  disabled?: boolean;
  accessibilityLabel?: string;
  /** 'radio' for single-choice lists such as the staff picker. */
  accessibilityRole?: 'button' | 'radio';
}

/** One person in the picker: avatar, name, a line under it, a tick when chosen. */
const PersonRow: React.FC<PersonRowProps> = ({
  leading,
  name,
  highlight,
  meta,
  metaTone = 'default',
  selected = false,
  error = false,
  trailing,
  onPress,
  disabled = false,
  accessibilityLabel,
  accessibilityRole = 'button',
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const nameStyle = [styles.name, selected && styles.nameSelected];

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || !onPress}
      accessibilityRole={accessibilityRole}
      accessibilityState={
        accessibilityRole === 'radio'
          ? { checked: selected, disabled }
          : { selected, disabled }
      }
      accessibilityLabel={accessibilityLabel || name}
      style={({ pressed, hovered }: any) => [
        styles.row,
        selected && styles.rowSelected,
        error && styles.rowError,
        (pressed || (Platform.OS === 'web' && hovered)) &&
          !selected &&
          styles.rowPressed,
      ]}>
      {leading}
      <View style={styles.text}>
        {highlight && highlight.hit ? (
          <Text style={nameStyle} numberOfLines={1}>
            {highlight.pre}
            <Text style={styles.nameHit}>{highlight.hit}</Text>
            {highlight.post}
          </Text>
        ) : (
          <Text style={nameStyle} numberOfLines={1}>
            {name}
          </Text>
        )}
        {meta ? (
          <Text
            style={[
              styles.meta,
              metaTone === 'error' && styles.metaError,
              selected && metaTone === 'default' && styles.metaSelected,
            ]}
            numberOfLines={1}>
            {meta}
          </Text>
        ) : null}
      </View>
      {trailing}
      {selected && !trailing ? (
        <View style={styles.tick}>
          <Feather
            name="check"
            size={iconSize(14)}
            color={theme.colors.textInverse}
          />
        </View>
      ) : null}
    </Pressable>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
      minHeight: v(52),
      paddingVertical: v(7),
      paddingHorizontal: s(12),
      borderRadius: s(14),
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    rowSelected: {
      backgroundColor: theme.colors.primarySurface,
    },
    rowPressed: {
      backgroundColor: theme.colors.surfaceSecondary,
    },
    rowError: {
      backgroundColor: theme.colors.errorSurface,
    },
    text: {
      flex: 1,
      minWidth: 0,
      gap: v(1),
    },
    name: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    nameSelected: {
      color: theme.colors.primaryDark,
    },
    nameHit: {
      fontFamily: GTFonts.bold,
      color: theme.colors.primaryDark,
    },
    meta: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textGray500,
    },
    metaSelected: {
      color: theme.colors.textGray600,
    },
    metaError: {
      color: theme.colors.errorDarker,
      fontFamily: GTFonts.semibold,
    },
    tick: {
      width: s(24),
      height: s(24),
      borderRadius: s(12),
      backgroundColor: theme.colors.primaryDark,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

export default PersonRow;
